import { createFileRoute, redirect, Outlet } from "@tanstack/react-router";
import { fetchSession, sessionQueryKey, appEnv } from "@/lib/auth";
import { setSessionUser } from "@/lib/sessionUser";
import { EnvBanner } from "@/components/shell/EnvBanner";
import { Navigation } from "@/components/shell/Navigation";
import { ScreenBarProvider } from "@/components/shell/ScreenBar";
import { KeyboardShortcutsDialog } from "@/components/shell/KeyboardShortcutsDialog";
import { ReleaseNotesDialog } from "@/features/releaseNotes/ReleaseNotesDialog";
import { useNotificationSignals } from "@/features/notifications/useNotificationSignals";
import { useIntakeOpener } from "@/features/intake/useIntakeOpener";

// Protected layout. The guard replaces the Next.js middleware + the
// `(shell)/layout.tsx` server-side session check.
export const Route = createFileRoute("/_shell")({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!session) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    // What the browser keeps per user (lib/listState, lib/sessionState) is kept under this one.
    setSessionUser(session.userId);
    return { session };
  },
  component: ShellLayout,
});

function ShellLayout() {
  // Show the non-production notice on every environment except production. The
  // env is resolved from the API at startup (loadAuthConfig), so this is stable
  // for the session — safe to fold into the static shell className.
  const showEnvBanner = appEnv() !== "production";
  // Tab title, favicon counter and desktop notifications. Mounted once, here,
  // because they are properties of the WINDOW and not of any one screen.
  useNotificationSignals();
  // A row the invoerbak's window opens comes up here, on the main screen (docs/invoerbak.md §3).
  useIntakeOpener();
  return (
    <div className={`wl-shell${showEnvBanner ? " has-env-banner" : ""}`}>
      {showEnvBanner && <EnvBanner />}
      {/* Around the navigation and the screen both: a screen puts its tabs in row 2. */}
      <ScreenBarProvider>
        <Navigation />
        <main className="wl-main">
          <Outlet />
        </main>
      </ScreenBarProvider>
      <KeyboardShortcutsDialog />
      <ReleaseNotesDialog />
    </div>
  );
}
